import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { SEO } from '../../utils/seo';

export default function HumanRightsPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Human Rights & Zero Exploitation Policy - LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
    "description": "Human Rights & Zero Exploitation Policy for LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS. Our commitment to upholding human rights and preventing exploitation.",
    "url": `${window.location.origin}/human-rights-policy`
  };

  return (
    <>
      <SEO
        title="Human Rights & Zero Exploitation Policy | LEWIS SECURITY North Wales"
        description="LEWIS SECURITY operates a zero-tolerance approach to modern slavery, human trafficking and forced labour. Learn about our commitment to upholding human rights across our operations and supply chain."
        keywords="human rights policy, zero exploitation, modern slavery, human trafficking, forced labour, ethical business, supply chain"
        schema={schema}
        canonical={`${window.location.origin}/human-rights-policy`}
      />
      <div className="min-h-screen bg-white font-inter">
        <Navbar />
        
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-8 font-poppins">Human Rights &amp; Zero Exploitation Policy</h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Our Commitment</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Lewis Security, Risk &amp; Compliance Solutions is committed to upholding human rights and ensuring that exploitation, abuse, coercion or unsafe working practices have no place in our operations or supply chain.
                </p>
                <div className="bg-orange-50 rounded-xl p-8 border border-orange-100">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-user-line text-2xl text-orange-600"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Zero-Tolerance Approach</h3>
                      <p className="text-gray-700 leading-relaxed">
                        We operate a <strong>zero-tolerance approach</strong> to modern slavery, human trafficking and forced labour in all aspects of our business and supply chain.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Our Principles</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We are committed to the following principles:
                </p>
                
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-scales-3-line text-xl text-orange-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Human Rights Act 1998</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Support the principles of the Human Rights Act 1998 and ensure all individuals are treated with dignity and respect.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-close-circle-line text-xl text-orange-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Zero Tolerance to Exploitation</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Operate a zero-tolerance approach to modern slavery, human trafficking and forced labour in all our operations and relationships.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-team-line text-xl text-orange-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Supply Chain Standards</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Expect the same standard from subcontractors, training partners and suppliers. All third parties must demonstrate their commitment to human rights.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-search-eye-line text-xl text-orange-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Act on Concerns</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Will act on concerns raised and cooperate fully with lawful investigations by relevant authorities.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-lock-line text-xl text-orange-600"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">Whistleblower Protection</h3>
                        <p className="text-gray-700 leading-relaxed">
                          Protect confidentiality for individuals who report wrongdoing and ensure no retaliation against those who raise concerns in good faith.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Scope of Application</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This policy applies to all aspects of our business operations, including:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-graduation-cap-line text-sm text-orange-600"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 font-poppins">Training Delivery</h4>
                    </div>
                    <p className="text-sm text-gray-600 pl-11">All training courses and educational services we provide</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-settings-line text-sm text-orange-600"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 font-poppins">Subcontracted Labour</h4>
                    </div>
                    <p className="text-sm text-gray-600 pl-11">All subcontractors and temporary workers engaged by us</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-lightbulb-line text-sm text-orange-600"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 font-poppins">Consultancy Projects</h4>
                    </div>
                    <p className="text-sm text-gray-600 pl-11">All consultancy and advisory services we deliver</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-links-line text-sm text-orange-600"></i>
                      </div>
                      <h4 className="font-bold text-gray-900 font-poppins">Supply Chain Relationships</h4>
                    </div>
                    <p className="text-sm text-gray-600 pl-11">All suppliers, partners and business relationships</p>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">What We Mean by Exploitation</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Exploitation can take many forms. We are committed to preventing and addressing:
                </p>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Modern Slavery &amp; Human Trafficking</h4>
                    <p className="text-sm text-gray-600">The recruitment, movement, harbouring or receiving of people through force, fraud or deception for the purpose of exploitation</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Forced Labour</h4>
                    <p className="text-sm text-gray-600">Any work or service extracted from a person under threat of penalty and for which they have not offered themselves voluntarily</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Debt Bondage</h4>
                    <p className="text-sm text-gray-600">Workers forced to work to pay off debts and unable to leave until the debt is repaid</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Unsafe Working Practices</h4>
                    <p className="text-sm text-gray-600">Working conditions that pose serious risks to health, safety or wellbeing</p>
                  </div>
                  <div className="bg-white rounded-lg p-5 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2 font-poppins">Coercion &amp; Abuse</h4>
                    <p className="text-sm text-gray-600">Physical, psychological or emotional abuse used to control or exploit individuals</p>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Due Diligence &amp; Risk Assessment</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We conduct appropriate due diligence on our supply chain and business relationships to identify and mitigate risks of exploitation. This includes:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Assessing potential partners and suppliers before engagement</li>
                  <li>Including human rights clauses in contracts and agreements</li>
                  <li>Monitoring compliance with our standards</li>
                  <li>Conducting periodic reviews of high-risk relationships</li>
                  <li>Providing training and awareness to staff and partners</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Reporting Concerns</h2>
                <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If you suspect or become aware of any form of exploitation, modern slavery, or human rights abuse in connection with our business, we encourage you to report it immediately.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    All reports will be treated seriously and investigated thoroughly. We guarantee confidentiality and protection from retaliation for anyone who reports concerns in good faith.
                  </p>
                  <div className="bg-white rounded-lg p-6 mt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 font-poppins">How to Report</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      If you have concerns about exploitation or human rights violations, please contact us immediately:
                    </p>
                    <div className="space-y-2">
                      <p className="text-gray-700">
                        <strong>Email:</strong> <a href="mailto:info@jl-security.co.uk" className="text-orange-600 hover:text-orange-700 font-medium">info@jl-security.co.uk</a>
                      </p>
                      <p className="text-gray-700">
                        <strong>Phone:</strong> <a href="tel:+447834561015" className="text-orange-600 hover:text-orange-700 font-medium">+44 7834 561015</a>
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">External Reporting Resources</h2>
                <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">Modern Slavery Helpline</h4>
                      <p className="text-gray-700 mb-1">Confidential support and advice on modern slavery</p>
                      <p className="text-gray-700">Phone: <a href="tel:08000121700" className="text-orange-600 hover:text-orange-700 font-medium">0800 0121 700</a></p>
                      <p className="text-gray-700">Website: <a href="https://www.modernslaveryhelpline.org" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 font-medium">www.modernslaveryhelpline.org</a></p>
                    </div>
                    
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">Gangmasters and Labour Abuse Authority (GLAA)</h4>
                      <p className="text-gray-700 mb-1">Report labour exploitation</p>
                      <p className="text-gray-700">Phone: <a href="tel:08000432210" className="text-orange-600 hover:text-orange-700 font-medium">0800 432 2210</a></p>
                      <p className="text-gray-700">Website: <a href="https://www.gla.gov.uk" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 font-medium">www.gla.gov.uk</a></p>
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">Police</h4>
                      <p className="text-gray-700 mb-1">Report suspected modern slavery or trafficking</p>
                      <p className="text-gray-700">Emergency: <a href="tel:999" className="text-orange-600 hover:text-orange-700 font-medium">999</a></p>
                      <p className="text-gray-700">Non-emergency: <a href="tel:101" className="text-orange-600 hover:text-orange-700 font-medium">101</a></p>
                    </div>

                    <div>
                      <h4 className="font-bold text-gray-900 mb-2 font-poppins">Crimestoppers</h4>
                      <p className="text-gray-700 mb-1">Anonymous reporting</p>
                      <p className="text-gray-700">Phone: <a href="tel:08005551111" className="text-orange-600 hover:text-orange-700 font-medium">0800 555 111</a></p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Training &amp; Awareness</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We provide appropriate training to our staff and partners to ensure they can:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Recognise signs of exploitation and modern slavery</li>
                  <li>Understand their responsibilities under this policy</li>
                  <li>Know how to report concerns appropriately</li>
                  <li>Conduct due diligence on suppliers and partners</li>
                  <li>Promote ethical business practices</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Consequences of Non-Compliance</h2>
                <div className="bg-red-50 border-l-4 border-red-600 p-6">
                  <p className="text-gray-900 font-semibold mb-2">
                    <i className="ri-alert-line text-red-600 mr-2"></i>
                    Serious Action
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Any breach of this policy will be taken extremely seriously and may result in <strong>immediate termination of contracts</strong>, <strong>removal from our supply chain</strong>, and <strong>referral to law enforcement authorities</strong>.
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Policy Review</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This policy is reviewed annually or following any significant incident or change in legislation. We are committed to continuous improvement in our human rights practices.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-poppins">Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions about this Human Rights &amp; Zero Exploitation Policy or wish to report a concern, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>LEWIS SECURITY, RISK &amp; COMPLIANCE SOLUTIONS</strong></p>
                  <p className="text-gray-700 mb-2">Email: <a href="mailto:info@jl-security.co.uk" className="text-orange-600 hover:text-orange-700 font-medium">info@jl-security.co.uk</a></p>
                  <p className="text-gray-700">Phone: <a href="tel:+447834561015" className="text-orange-600 hover:text-orange-700 font-medium">+44 7834 561015</a></p>
                </div>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <Link 
                to="/" 
                className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
